import { Injectable } from '@angular/core';

import {
  CompressedEmojiData,
  EmojiData,
  EmojiVariation,
} from './data/data.interfaces';
import { emojis } from './data/emojis';
import { Emoji } from './emoji.component';

export const EMOJI_COLON = '*';
export const SKIN_TONE_COLON = ':';
const COLONS_REGEX = new RegExp(`^(?:\\${EMOJI_COLON}([^\\${EMOJI_COLON}]+)\\${EMOJI_COLON})(?:\\${SKIN_TONE_COLON}skin-tone-(\\d)\\${SKIN_TONE_COLON})?$`);
const SKINS = ['1F3FA', '1F3FB', '1F3FC', '1F3FD', '1F3FE', '1F3FF'];
export const DEFAULT_BACKGROUNDFN = (
  set: string,
  sheetSize: number,
) => `url(./assets/images/${set}/sheets-256/${sheetSize}.png)`;


@Injectable({ providedIn: 'root' })
export class EmojiService {
  uncompressed = false;
  names: { [key: string]: EmojiData } = {};
  emojis: EmojiData[] = [];

  constructor() {
    if (!this.uncompressed) {
      this.uncompress(emojis);
      this.uncompressed = true;
    }
  }

  uncompress(list: CompressedEmojiData[]) {
    this.emojis = list.map(emoji => {

      const data: any = { ...emoji };

      if (!data.shortNames) {
        data.shortNames = [];
      }
      data.shortNames.unshift(data.shortName);
      data.id = data.shortName;
      data.native = this.unifiedToNative(data.unified);

      if (!data.skinVariations) {
        data.skinVariations = [];
      }

      if (!data.keywords) {
        data.keywords = [];
      }

      if (!data.emoticons) {
        data.emoticons = [];
      }

      if (!data.hidden) {
        data.hidden = [];
      }

      if (!data.text) {
        data.text = '';
      }

      if (data.obsoletes) {
        // get keywords from emoji that it obsoletes since that is shared
        const f = list.find(x => x.unified === data.obsoletes);
        if (f) {
          if (f.keywords) {
            data.keywords = [...data.keywords, ...f.keywords, f.shortName];
          } else {
            data.keywords = [...data.keywords, f.shortName];
          }
        }
      }

      this.names[data.unified] = data;

      for (const n of data.shortNames) {
        this.names[n] = data;
      }

      return data;
    });
  }

  unifiedToNative(unified: string) {
    const codePoints = unified.split('-').map(u => parseInt(`0x${u}`, 16));
    return String.fromCodePoint(...codePoints);
  }

  emojiSpriteStyles(
    sheet: EmojiData['sheet'],
    set: Emoji['set'] = 'apple',
    size: Emoji['size'] = 24,
    sheetSize: Emoji['sheetSize'] = 64,
    backgroundImageFn: Emoji['backgroundImageFn'] = DEFAULT_BACKGROUNDFN,
    sheetColumns = 52,
    ) {
    return {
      width: `${size}px`,
      height: `${size}px`,
      display: 'inline-block',
      'background-image': `url(${backgroundImageFn(set, sheetSize)})`,
      'background-size': `${100 * sheetColumns}%`,
      'background-position': this.getSpritePosition(sheet, sheetColumns),
    };
  }

  getSpritePosition(sheet: EmojiData['sheet'], sheetColumns: number) {
    const [sheetX, sheetY] = sheet;
    const multiply = 100 / (sheetColumns - 1);
    return `${multiply * sheetX}% ${multiply * sheetY}%`;
  }

  getData(
    emoji: EmojiData | string,
    skin?: Emoji['skin'],
    set?: Emoji['set'],
  ): EmojiData | null {
    let emojiData: any;

    if (typeof emoji === 'string') {
      const matches = emoji.match(COLONS_REGEX);

      if (matches) {
        emoji = matches[1];

        if (matches[2]) {
          skin = parseInt(matches[2], 10) as Emoji['skin'];
        }
      }
      if (this.names.hasOwnProperty(emoji)) {
        emojiData = this.names[emoji];
      } else {
        return null;
      }
    } else if (emoji.id) {
      emojiData = this.names[emoji.id];
    } else if (emoji.unified) {
      emojiData = this.names[emoji.unified.toUpperCase()];
    }

    if (!emojiData) {
      emojiData = emoji;
      emojiData.custom = true;
    }

    const hasSkinVariations = emojiData.skinVariations && emojiData.skinVariations.length;
    if (hasSkinVariations && skin && skin > 1 && set) {
      emojiData = { ...emojiData };

      const skinKey = SKINS[skin - 1];
      const variationData = emojiData.skinVariations.find((n: EmojiVariation) =>
        n.unified.includes(skinKey),
      );

      if (!variationData.hidden || !variationData.hidden.includes(set)) {
        emojiData.skinTone = skin;
        emojiData = { ...emojiData, ...variationData };
      }
      emojiData.native = this.unifiedToNative(emojiData.unified);
    }

    emojiData.set = set || '';
    return emojiData as EmojiData;
  }

  sanitize(emoji: EmojiData | null): EmojiData | null {
    if (emoji === null) {
      return null;
    }
    const id = emoji.id || emoji.shortNames[0];
    let colons = `${EMOJI_COLON}${id}${EMOJI_COLON}`;
    if (emoji.skinTone) {
      colons += `${SKIN_TONE_COLON}skin-tone-${emoji.skinTone}${SKIN_TONE_COLON}`;
    }
    emoji.colons = colons;
    return { ...emoji };
  }

  getSanitizedData(
    emoji: string | EmojiData,
    skin?: Emoji['skin'],
    set?: Emoji['set'],
  ) {
    return this.sanitize(this.getData(emoji, skin, set));
  }
}
